import { router } from '@inertiajs/react'
import { useMemo, useRef, useState } from 'react'
import {
  EmailTestBatchResult,
  EmailTestCatalogEntry,
  EmailTestMessageResult,
  SystemEmailSetting,
} from '../../types'

const TONE_CLASSES: Record<string, string> = {
  suppressed: 'bg-[#F5F5F5] text-text-primary border-stone/30',
  pending: 'bg-[#F5F5F5] text-text-primary border-stone/30',
  accepted: 'bg-[#351E1D]/5 text-text-primary border-charcoal/30',
  success: 'bg-[#E8F5EC] text-[#15532E] border-[#15532E]/30',
  failure: 'bg-[#FDECEC] text-[#ED1C24] border-[#ED1C24]/30',
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function newToken() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * The Super Admin test facility.
 *
 * Every message goes to the one address typed here — that address is never
 * looked up as, or created as, a customer. Sends are queued, so "queued",
 * "accepted by SendGrid" and "delivery confirmed" stay three distinct things
 * and none of them is presented as proof the message reached an inbox.
 */
export default function EmailTestPanel({
  catalog,
  batch,
  setting,
  superAdminEmail,
  history,
  busy,
  unsaved,
}: {
  catalog: EmailTestCatalogEntry[]
  batch: EmailTestBatchResult | null
  setting: SystemEmailSetting
  superAdminEmail: string | null
  history: { id: number; recipient_email: string; created_at_display: string }[]
  busy: boolean
  unsaved: boolean
}) {
  const [recipient, setRecipient] = useState(batch?.recipient_email || '')
  const [selected, setSelected] = useState<string[]>(catalog.filter(c => c.audience !== 'guide' || setting.guide_from_email).map(c => c.key))
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  // Held across a failed round-trip so a resubmission of the same intent is
  // deduplicated server-side rather than sending everything twice.
  const tokenRef = useRef<string | null>(null)
  const posting = useRef(false)

  const configured = setting.operator_tests_allowed
  const guideConfigured = Boolean(setting.guide_from_email)
  const recipientValid = EMAIL_PATTERN.test(recipient.trim())

  const groups = useMemo(() => {
    const order: string[] = []
    const byGroup: Record<string, { label: string; entries: EmailTestCatalogEntry[] }> = {}
    catalog.forEach(entry => {
      if (!byGroup[entry.group]) {
        byGroup[entry.group] = { label: entry.group_label, entries: [] }
        order.push(entry.group)
      }
      byGroup[entry.group].entries.push(entry)
    })
    return order.map(key => ({ key, ...byGroup[key] }))
  }, [catalog])

  const resultsByKey = useMemo(() => {
    const map: Record<string, EmailTestMessageResult> = {}
    batch?.messages.forEach(m => { map[m.catalog_key] = m })
    return map
  }, [batch])

  const post = (keys: string[]) => {
    setLocalError(null)
    if (!recipientValid) {
      setLocalError('Enter a valid recipient email address first.')
      return
    }
    if (keys.length === 0) {
      setLocalError('Select at least one email type to test.')
      return
    }
    if (keys.some(key => catalog.find(entry => entry.key === key)?.audience === 'guide') && !guideConfigured) {
      setLocalError('Save a verified Guide From address before sending Guide samples.')
      return
    }
    if (posting.current || busy || unsaved || !configured) return

    if (!tokenRef.current) tokenRef.current = newToken()
    posting.current = true
    setSubmitting(true)
    router.post(
      '/super_admin/email_setting/send_tests',
      { recipient: recipient.trim(), catalog_keys: keys, submission_token: tokenRef.current },
      {
        preserveScroll: true,
        onSuccess: () => { tokenRef.current = null; setConfirming(false) },
        onFinish: () => { posting.current = false; setSubmitting(false) },
      }
    )
  }

  const retry = (message: EmailTestMessageResult) => {
    if (posting.current || busy || unsaved || !configured || !message.retryable) return
    posting.current = true
    setSubmitting(true)
    router.post(
      '/super_admin/email_setting/retry_test',
      { message_id: message.id },
      { preserveScroll: true, onFinish: () => { posting.current = false; setSubmitting(false) } }
    )
  }

  const toggle = (key: string) => {
    setSelected(s => (s.includes(key) ? s.filter(k => k !== key) : [...s, key]))
  }

  return (
    <div className="bg-white border border-stone/20 rounded-lg p-6">
      <h2 className="text-base font-semibold text-text-primary mb-1">Test outbound email</h2>
      <p className="text-xs text-text-muted mb-4">
        Sends a sample copy of every email Pinnacle currently produces, rendered by the same production
        templates, to one address you choose. Samples use fictional people and companies, carry a{' '}
        <code>[Sample]</code> subject and an in-body sample banner, and their sign-in, invitation and
        confirmation links are inert — nobody is invited, no reset token is issued, and no billing or meeting
        state changes. In Test only and Live, these are real sends to the chosen inbox only, not SendGrid
        sandbox validation. Off sends nothing. Changing mode does not recall messages already accepted by SendGrid.
        Client and Guide samples use their respective saved From addresses; each result records the sender
        actually selected when the worker submitted it.
      </p>

      <div className="mb-4">
        <label className="block text-xs font-semibold text-text-primary mb-1" htmlFor="test-recipient">
          Send all test emails to
        </label>
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <input
              id="test-recipient"
              type="email"
              value={recipient}
               onChange={e => { setRecipient(e.target.value); setLocalError(null); tokenRef.current = null; setConfirming(false) }}
               disabled={submitting}
              placeholder="you@yourdomain.com"
              className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
            />
            <p className="text-xs text-text-muted mt-1">
              Any inbox you control. It does not need a Pinnacle account, and it is never looked up or created
              as a customer — even if the address happens to match one.
            </p>
          </div>
          {superAdminEmail && (
            <button
              type="button"
              onClick={() => { setRecipient(superAdminEmail); tokenRef.current = null; setConfirming(false) }}
              disabled={submitting}
              className="px-3 py-2 border border-stone/40 rounded-lg text-xs font-semibold text-text-primary hover:bg-[#F5F5F5] whitespace-nowrap"
            >
              Use my address
            </button>
          )}
        </div>
      </div>

      {!configured && (
        <p className="text-xs text-[#ED1C24] mb-3">
          Save an API key and verified From address, and select Test only or Live before sending tests. Off sends nothing.
        </p>
      )}
      {!guideConfigured && (
        <p className="text-xs text-text-primary mb-3">
          Guide samples need a saved Guide From address. Client samples can still be sent separately.
        </p>
      )}
      {unsaved && <p className="text-sm text-text-primary mb-3">Save your configuration changes before sending a test.</p>}
      {localError && <p className="text-xs text-[#ED1C24] mb-3">{localError}</p>}
      {history.length > 0 && (
        <div className="mb-4">
          <label htmlFor="email-test-history" className="block text-xs font-semibold text-text-primary mb-1">Saved test runs (most recent 30)</label>
          <select id="email-test-history" value={batch?.id || ''} disabled={busy || submitting}
            onChange={e => router.get('/super_admin/email_setting', { batch_id: e.target.value }, { preserveState: true, preserveScroll: true })}
            className="w-full border border-stone/30 rounded-lg p-2 text-sm">
            {history.map(run => <option key={run.id} value={run.id}>Run {run.id} · {run.created_at_display} · {run.recipient_email}</option>)}
          </select>
          {batch && <p className="text-xs text-text-muted mt-1">Results below are for {batch.recipient_email}. New tests use the address entered above. Retrying a failed result uses its original recipient.</p>}
        </div>
      )}

      <div className="space-y-4">
        {groups.map(group => (
          <div key={group.key}>
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1.5">
              {group.label}
            </div>
            <ul className="divide-y divide-stone/10 border border-stone/15 rounded-lg">
              {group.entries.map(entry => (
                <CatalogRow
                  key={entry.key}
                  entry={entry}
                  result={resultsByKey[entry.key]}
                  checked={selected.includes(entry.key)}
                  onToggle={() => toggle(entry.key)}
                  onSend={() => post([entry.key])}
                  onRetry={retry}
                  disabled={!configured || submitting || busy || unsaved}
                  guideConfigured={guideConfigured}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-stone/10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-xs">
            <button type="button" onClick={() => setSelected(catalog.filter(c => c.audience !== 'guide' || guideConfigured).map(c => c.key))} className="underline font-semibold text-text-primary">
              Select all
            </button>
            <button type="button" onClick={() => setSelected([])} className="underline font-semibold text-text-primary">
              Clear
            </button>
            <span className="text-text-muted">{selected.length} of {catalog.length} selected</span>
          </div>
          <button
            type="button"
            onClick={() => { setLocalError(null); setConfirming(true) }}
            disabled={!configured || submitting || busy || unsaved || selected.length === 0}
            className="px-4 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50"
          >
             {selected.length === catalog.length ? 'Send all test emails' : 'Send selected test emails'}
          </button>
        </div>
      </div>

      {confirming && (
        <ConfirmSend
          recipient={recipient.trim()}
          count={selected.length}
          valid={recipientValid}
          submitting={submitting || busy || unsaved}
          onCancel={() => setConfirming(false)}
          onConfirm={() => post(selected)}
        />
      )}

      {batch && <BatchSummary batch={batch} />}
    </div>
  )
}

function CatalogRow({
  entry,
  result,
  checked,
  onToggle,
  onSend,
  onRetry,
  disabled,
  guideConfigured,
}: {
  entry: EmailTestCatalogEntry
  result?: EmailTestMessageResult
  checked: boolean
  onToggle: () => void
  onSend: () => void
  onRetry: (message: EmailTestMessageResult) => void
  disabled: boolean
  guideConfigured: boolean
}) {
  const senderReady = entry.audience !== 'guide' || guideConfigured
  return (
    <li className="p-3 flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-0.5 w-4 h-4 flex-shrink-0"
        aria-label={`Include ${entry.label}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-text-primary">{entry.label}</div>
        <div className="text-xs text-text-muted">{entry.description}</div>
        <div className="text-xs text-text-muted">Sender: {entry.audience === 'guide' ? 'Guide' : 'Client'}</div>
        {result && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold ${TONE_CLASSES[result.status_tone] || TONE_CLASSES.pending}`}>
              {result.status_label}
            </span>
            {result.rendering_mode_label && (
              <span className="text-xs text-text-muted">{result.rendering_mode_label}</span>
            )}
            <span className="text-xs text-text-muted">· {result.delivery_path_label}</span>
            {result.sender_email && (
              <span className="text-xs text-text-muted">
                · Sent as {result.sender_name ? `${result.sender_name} <${result.sender_email}>` : result.sender_email} ({result.sender_audience})
              </span>
            )}
            {result.updated_at_display && (
              <span className="text-xs text-text-muted">· {result.updated_at_display}</span>
            )}
          </div>
        )}
        {result && <div className="text-xs text-text-muted mt-1">{result.status_detail}</div>}
        {result?.template_revision_label && <div className="text-xs text-text-muted mt-1">{result.template_revision_label}</div>}
        {result?.awaiting_confirmation && (
          <div className="text-xs text-[#8A6A12] mt-0.5">{result.awaiting_confirmation}</div>
        )}
        {result?.error_message && <div className="text-xs text-[#ED1C24] mt-0.5">{result.error_message}</div>}
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <button
          type="button"
          onClick={onSend}
          disabled={disabled || !senderReady}
          className="px-3 py-1.5 bg-[#351E1D] text-white rounded-lg text-xs font-semibold hover:bg-[#4A3B3A] disabled:opacity-50 whitespace-nowrap"
        >
          Send this one
        </button>
        {result?.retryable && (
          <button
            type="button"
            onClick={() => onRetry(result)}
            disabled={disabled}
            className="px-3 py-1.5 border border-stone/40 rounded-lg text-xs font-semibold text-text-primary hover:bg-[#F5F5F5] disabled:opacity-50 whitespace-nowrap"
          >
            Retry
          </button>
        )}
      </div>
    </li>
  )
}

function ConfirmSend({
  recipient,
  count,
  valid,
  submitting,
  onCancel,
  onConfirm,
}: {
  recipient: string
  count: number
  valid: boolean
  submitting: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div className="mt-4 border border-[#ED1C24]/30 bg-[#FDECEC] rounded-lg p-4">
      <p className="text-sm font-semibold text-text-primary">Send {count} test {count === 1 ? 'email' : 'emails'}?</p>
      <p className="text-xs text-text-muted mt-1">
        All {count} {count === 1 ? 'message goes' : 'messages go'} to{' '}
        <span className="font-semibold text-text-primary">{valid ? recipient : '(no valid recipient)'}</span> and
        nowhere else. Nothing is sent to customers, and nothing is delivered until a background worker picks
        each message up.
      </p>
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting || !valid}
          className="px-4 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50"
        >
          {submitting ? 'Queueing…' : `Yes, send ${count}`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 border border-stone/40 rounded-lg text-sm font-semibold text-text-primary hover:bg-white disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function BatchSummary({ batch }: { batch: EmailTestBatchResult }) {
  return (
    <div className="mt-5 pt-4 border-t border-stone/10">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h3 className="text-sm font-semibold text-text-primary">
          Last run — {batch.messages.length} {batch.messages.length === 1 ? 'message' : 'messages'} to{' '}
          {batch.recipient_email}
        </h3>
        <span className="text-xs text-text-muted">{batch.created_at_display}</span>
      </div>
      <p className="text-xs text-text-muted">
        {batch.in_progress
          ? 'Still in progress — results refresh automatically.'
          : 'Results are stored, so they survive a refresh.'}{' '}
        <span className="font-semibold text-text-primary">Accepted</span> means SendGrid took the request;
        only a signed <span className="font-semibold text-text-primary">delivered</span> event confirms
        delivery. Neither is proof of inbox placement or correct rendering — open the messages and read them.
      </p>
    </div>
  )
}
